import type { FC } from 'react'

import { Button } from '@skytech/ui/Button'
import { getDateFormat } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import FileDelimitedIcon from 'mdi-react/FileDelimitedIcon'
import MicrosoftExcelIcon from 'mdi-react/MicrosoftExcelIcon'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useIsThreeStepIT } from '@/hooks/access'
import queries from '@/modules/admin/queries'

import { exportDataToExcel } from '../../helpers'
import type { ReportFilters, ReportSetupExportSettings } from '../../resources'
import adminResources from '../../resources'

export const TaxAdvantageOptions: FC<{
  title: string
  filters: ReportFilters
  reportSetupExportSettings?: ReportSetupExportSettings
}> = ({ filters, title }) => {
  const { data: reports } = useQuery(queries.adminTaxAdvantageReports(filters), () =>
    adminResources.getTaxAdvantageReports(filters)
  )
  const translate = useTranslation()
  const isThreeStepIT = useIsThreeStepIT()

  if (!reports) throw Error('We have encountered a problem while loading reports')

  const { fromDate, toDate } = filters
  if (!fromDate || !toDate) throw Error('Missing date range filters!')

  const reportsData = reports.map(report => ({
    name: report.name,
    employeeNo: report.employeeNo,
    id: report.id,
    salaryDeductionAmount: report.salaryDeductionAmount,
    contentServiceAmount: report.contentServiceAmount,
    benefitAmount: report.benefitAmount,
    amount: report.amount,
    vat: report.vat,
    benefitMobile: report.benefitMobile,
    orgNo: report.orgNo,
    company: report.company,
    startMonth: report.startMonth,
    endMonth: report.endMonth,
    monthNo: report.monthNo,
    taxTmp: report.taxTmp,
    taxAmount: report.taxAmount,
  }))

  const columnFormats = {
    amount: 'amount',
    salaryDeductionAmount: 'amount',
    benefitAmount: 'amount',
    contentServiceAmount: 'amount',
    vat: 'amount',
  }
  return (
    <div className="grid grid-cols-2">
      <Button
        Icon={<MicrosoftExcelIcon className={twJoin(!isThreeStepIT && 'text-emerald-600')} />}
        className="mb-4 ml-0"
        onClick={() =>
          exportDataToExcel(reportsData, title, {
            translate,
            columnFormats,
            fromDate: getDateFormat(new Date(Number(filters.year), 0, 1), 'YYYY-MM-DD'),
            toDate: getDateFormat(new Date(Number(filters.year), 11, 31), 'YYYY-MM-DD'),
          })
        }
      >
        <Translation id="Excel" />
      </Button>
      <Button
        Icon={<FileDelimitedIcon className={twJoin(!isThreeStepIT && 'text-emerald-600')} />}
        className="mb-4 ml-0"
        onClick={() =>
          exportDataToExcel(reports, title, {
            extension: 'csv',
            translate,
            columnFormats,
            fromDate: getDateFormat(new Date(Number(filters.year), 0, 1), 'YYYY-MM-DD'),
            toDate: getDateFormat(new Date(Number(filters.year), 11, 31), 'YYYY-MM-DD'),
          })
        }
      >
        <Translation id="CSV" />
      </Button>
    </div>
  )
}
